import { put, putResolve, select, call, spawn } from "redux-saga/effects";
import {
  Lodging,
  AvailabilityRequestEnum,
  AvailabilityRequest,
  PlatformEnum,
  LodgingSelection,
  LodgingSelectionEnum,
  ShopResponseEnum,
  IShopResponseBase,
  IShopResponseAvailable,
  LodgingCollectionEnum,
  decodePersistentLodgingTrackingProperties,
  encodePersistentLodgingTrackingProperties,
  IPersistentLodgingTrackingProperties,
  HotelDetailsEntrySourceEnum,
  DatelessSearchSource,
  transformToShopDetailsStringifiedQuery,
  AmenityEnum,
} from "redmond";
import dayjs from "dayjs";
import queryStringParser from "query-string";

import {
  getIsTravelApp,
  localCache,
  trackEngagementEvent,
} from "@capone/common";
import { IStoreState } from "../../../reducers/types";
import Logger from "../../../utils/logger";
import { fetchPremierCollectionShop } from "../../../api/v0/shop/fetchPremierCollectionShop";
import {
  PremierCollectionShopCallState,
  PremierCollectionShopCallError,
} from "../reducer";
import { getPremierCollectionShopSelectedAvailability } from "../reducer/selectors";
import {
  getAdultsCount,
  getChildrenCount,
  getFromDate,
  getUntilDate,
} from "../../search/reducer";
import { actions as searchActions } from "../../search/actions";
import { actions as availabilityActions } from "../../availability/actions";
import { actions } from "../actions";
import { isMobile } from "../../../utils/userAgent";
import { IPremierCollectionShopParsedQuery } from "../utils/queryStringHelpers";
import {
  getPremierCollectionAvailabilityFromDate,
  getPremierCollectionAvailabilityLodgings,
  getPremierCollectionAvailabilityUntilDate,
  getSearchAdultsCount,
  getSearchChildrenCount,
  getSelectedLodgingIndex,
} from "../../availability/reducer";
import { fetchPremierCollectionAvailability } from "../../../api/v0/availability/fetchPremierCollectionAvailability";
import { hydrateSelectedLocationSaga } from "./hydrateSelectedLocationSaga";
import { getRewardsAccountsReferenceIds } from "../../rewards/reducer";
import { PATH_SHOP_DETAILS } from "../../../utils/paths";

const DEFAULT_CHECKIN_DATE_OFFSET_IN_DAYS_FROM_TODAY = 1;
const DEFAULT_CHECKOUT_DATE_OFFSET_IN_DAYS_FROM_CHECKIN = 2;

export default function* fetchPremierCollectionShopSaga(
  fetchPremierCollectionShopAction: actions.IFetchPremierCollectionShop
) {
  try {
    const {
      selectedAvailability,
      shopToken,
      isTravelApp,
    }: { selectedAvailability?: Lodging; shopToken?: string; isTravelApp?: boolean } =
      // eslint-disable-next-line no-use-before-define
      yield call(setUpPremierCollectionShopParameters, fetchPremierCollectionShopAction);
    const { history } = fetchPremierCollectionShopAction;
    // TODO: add dates when implemented on LUX-164
    if (selectedAvailability && !selectedAvailability.available) {
      const shopDetailsParams = transformToShopDetailsStringifiedQuery({
        lodgingId: selectedAvailability.lodging.id,
        unavailable: true,
        source: DatelessSearchSource.Shop,
        ...(isTravelApp ? { isTravelApp: true } : {}),
      });
      history.replace({
        pathname: PATH_SHOP_DETAILS,
        search: shopDetailsParams,
      });
      return;
    }

    const state: IStoreState = yield select();
    const fromDate: Date = yield getFromDate(state);
    const untilDate: Date = yield getUntilDate(state);
    const accountReferenceIds: string[] = getRewardsAccountsReferenceIds(state);

    const shopRequestId = selectedAvailability?.price?.opaqueShopRequest;

    if (!shopRequestId && !shopToken) {
      throw new Error("Shop Request Id must be present.");
    }

    let response: IShopResponseBase;
    try {
      response = yield fetchPremierCollectionShop({
        opaqueRequest: shopToken ?? shopRequestId!,
      });
    } catch (initialShopError) {
      // The URL-derived shopToken can resolve to a stale JWT minted in an
      // earlier session; its embedded additionalData won't match the current
      // session and the shop endpoint rejects it with a crypto verification
      // error. Retry once with the freshly-minted opaqueShopRequest from the
      // availability response we just made.
      if (shopToken && shopRequestId && shopToken !== shopRequestId) {
        response = yield fetchPremierCollectionShop({
          opaqueRequest: shopRequestId,
        });
      } else {
        throw initialShopError;
      }
    }

    if (response.ShopResponse === ShopResponseEnum.Unavailable) {
      yield put(
        actions.setPremierCollectionShopCallStateFailed({
          premierCollectionShopCallError:
            PremierCollectionShopCallError.NoAvailability,
        })
      );
      return;
    }
    if (
      response.ShopResponse === ShopResponseEnum.Failure ||
      response.lodgingCollection === LodgingCollectionEnum.NoCollection // Adding this to shop modal if PC/LC returns NoCollection instead of PC/LC due to shop token changes
    ) {
      yield put(
        actions.setPremierCollectionShopCallStateFailed({
          premierCollectionShopCallError:
            PremierCollectionShopCallError.Unknown,
        })
      );
      return;
    }
    const availableResponse = response as IShopResponseAvailable;
    yield putResolve(
      actions.setPremierCollectionShopResults({
        premierCollectionShopCallState: PremierCollectionShopCallState.Success,
        payload: {
          roomInfoProducts: availableResponse.roomInfoProducts,
          trackingPropertiesV2: availableResponse.trackingPropertiesV2,
          cancellationSummary: availableResponse.cancellationSummary,
        },
      })
    );

    trackEngagementEvent({
      event: {
        event_type: "hotel_viewed",
        lodging_id: availableResponse.lodgingId,
        lodging_name: selectedAvailability?.lodging.name,
        lowest_room_price:
          availableResponse.roomInfoProducts[0].products[0].nightlyDiscountAware
            .priceWithUnmanagedDiscounts.fiat.value ??
          availableResponse.roomInfoProducts[0].products[0].perNightSellRate
            .fiat.value,
        lodging_country: selectedAvailability?.lodging.country,
        lodging_city: selectedAvailability?.lodging.city,
        check_in_date_timestamp: dayjs(fromDate).valueOf(),
        check_out_date_timestamp: dayjs(untilDate).valueOf(),
        coordinates: selectedAvailability?.lodging.location.coordinates,
        account_reference_ids: accountReferenceIds,
        lodging_type: selectedAvailability?.lodgingCollection,
      },
    });
  } catch (e) {
    Logger.debug(e);
    yield put(
      actions.setPremierCollectionShopCallStateFailed({
        premierCollectionShopCallError: PremierCollectionShopCallError.Unknown,
      })
    );
  }
}

function parseQueryString(
  fetchPremierCollectionShopAction: actions.IFetchPremierCollectionShop,
  now: dayjs.Dayjs
) {
  const queryString = fetchPremierCollectionShopAction.history.location.search;
  const parsedQueryStringPrimitive = queryStringParser.parse(queryString);
  const { isTravelApp } = getIsTravelApp(queryString);

  let persistentLodgingTrackingProperties:
    | IPersistentLodgingTrackingProperties
    | undefined;
  try {
    if (parsedQueryStringPrimitive.trackingProperties) {
      persistentLodgingTrackingProperties =
        decodePersistentLodgingTrackingProperties(
          parsedQueryStringPrimitive.trackingProperties as string
        ) ?? undefined;
    }
  } catch (e) {
    Logger.debug(e);
  }

  const parsedQueryString: IPremierCollectionShopParsedQuery = {
    lodgingId: parsedQueryStringPrimitive.lodgingId as string,
    fromDate: parsedQueryStringPrimitive.fromDate as string,
    untilDate: parsedQueryStringPrimitive.untilDate as string,
    adultsCount: Number(parsedQueryStringPrimitive.adultsCount || 2),
    childrenCount: Number(parsedQueryStringPrimitive.childrenCount || 0),
    selectedLodgingIndex: Number(
      parsedQueryStringPrimitive.selectedLodgingIndex
    ),
    fromHotelAvailability: !!parsedQueryStringPrimitive.fromHotelAvailability,
    shopToken: parsedQueryStringPrimitive.shopToken as string,
    isFromPremiumOffers: !!parsedQueryStringPrimitive.isFromPremiumOffers,
    persistentLodgingTrackingProperties,
    hotelDetailsEntrySource:
      parsedQueryStringPrimitive.hotelDetailsEntrySource as HotelDetailsEntrySourceEnum,
    isTravelApp,
  };

  try {
    parsedQueryString.lodgingSelection = parsedQueryString.fromHotelAvailability
      ? (JSON.parse(
          decodeURIComponent(
            parsedQueryStringPrimitive.lodgingSelection as string
          )
        ) as LodgingSelection)
      : (parsedQueryStringPrimitive.lodgingSelection as string);
  } catch (e) {
    Logger.debug(e);
  }

  const currentDate = dayjs().toDate();
  currentDate.setHours(0);
  currentDate.setMinutes(0);
  currentDate.setSeconds(0);
  currentDate.setMilliseconds(0);

  const parsedQueryStringFromDate: Date =
    parsedQueryString.fromDate &&
    dayjs(parsedQueryString.fromDate).toDate() >= currentDate
      ? dayjs(parsedQueryString.fromDate).toDate()
      : now
          .add(DEFAULT_CHECKIN_DATE_OFFSET_IN_DAYS_FROM_TODAY, "days")
          .toDate();
  const parsedQueryStringUntilDate: Date =
    parsedQueryString.untilDate &&
    dayjs(parsedQueryString.untilDate).toDate() > parsedQueryStringFromDate
      ? dayjs(parsedQueryString.untilDate).toDate()
      : dayjs(parsedQueryStringFromDate)
          .add(DEFAULT_CHECKOUT_DATE_OFFSET_IN_DAYS_FROM_CHECKIN, "days")
          .toDate();
  const parsedQueryStringLocationName =
    (parsedQueryStringPrimitive?.lodgingSelection as string) || "";

  return {
    parsedQueryString,
    parsedQueryStringFromDate,
    parsedQueryStringUntilDate,
    parsedQueryStringAdultsCount: parsedQueryString.adultsCount,
    parsedQueryStringChildrenCount: parsedQueryString.childrenCount,
    parsedQueryStringSelectedLodgingIndex:
      parsedQueryString.selectedLodgingIndex,
    parsedQueryStringLodgingSelection: parsedQueryString.lodgingSelection,
    parsedQueryStringIsFromHotelAvailability:
      !!parsedQueryString.fromHotelAvailability,
    parsedQueryStringLocationName,
    parsedQueryStringShopToken: parsedQueryString.shopToken,
    parsedQueryStringIsFromPremiumOffers:
      !!parsedQueryString.isFromPremiumOffers,
    parsedQueryStringPersistentLodgingTrackingProperties:
      parsedQueryString.persistentLodgingTrackingProperties,
    parsedQueryStringIsTravelApp: isTravelApp,
  };
}

function* setUpPremierCollectionShopParameters(
  fetchPremierCollectionShopAction: actions.IFetchPremierCollectionShop
) {
  const state: IStoreState = yield select();
  let selectedAvailability: Lodging =
    yield getPremierCollectionShopSelectedAvailability(state);
  let fromDate: Date = yield getFromDate(state);
  let untilDate: Date = yield getUntilDate(state);
  const adultsCount: number = yield getAdultsCount(state);
  const childrenCount: number = yield getChildrenCount(state);
  const selectedLodgingIndex: number = yield getSelectedLodgingIndex(state);
  let lodgingToken: string | undefined;
  const { history } = fetchPremierCollectionShopAction;
  const now = dayjs();
  const parsed = parseQueryString(fetchPremierCollectionShopAction, now);
  let {
    parsedQueryString,
    parsedQueryStringFromDate,
    parsedQueryStringUntilDate,
    parsedQueryStringAdultsCount,
    parsedQueryStringChildrenCount,
    parsedQueryStringSelectedLodgingIndex,
    parsedQueryStringShopToken,
  } = parsed;
  const {
    parsedQueryStringLodgingSelection,
    parsedQueryStringIsFromHotelAvailability,
    parsedQueryStringLocationName,
    parsedQueryStringIsFromPremiumOffers,
    parsedQueryStringPersistentLodgingTrackingProperties,
    parsedQueryStringIsTravelApp,
  } = parsed;

  yield put(
    actions.setIsFromHotelAvailability(parsedQueryStringIsFromHotelAvailability)
  );

  yield put(
    actions.setIsFromPremiumOffers(parsedQueryStringIsFromPremiumOffers)
  );

  if (parsedQueryStringPersistentLodgingTrackingProperties) {
    yield put(
      actions.setPersistentLodgingTrackingProperties(
        parsedQueryStringPersistentLodgingTrackingProperties
      )
    );
  }

  if (!fetchPremierCollectionShopAction.options?.overrideStateByQueryParams) {
    if (
      selectedAvailability &&
      fromDate &&
      untilDate &&
      (parsedQueryString.lodgingId !== selectedAvailability.lodging.id ||
        parsedQueryStringFromDate !== fromDate ||
        parsedQueryStringUntilDate !== untilDate)
    ) {
      history.replace({
        ...history.location,
        search: queryStringParser.stringify({
          lodgingId: selectedAvailability.lodging.id,
          fromDate: dayjs(fromDate).format("YYYY-MM-DD"),
          untilDate: dayjs(untilDate).format("YYYY-MM-DD"),
          adultsCount,
          childrenCount,
          selectedLodgingIndex,
          lodgingSelection: encodeURIComponent(
            JSON.stringify(parsedQueryStringLodgingSelection)
          ),
          ...(parsedQueryStringPersistentLodgingTrackingProperties
            ? {
                trackingProperties: encodePersistentLodgingTrackingProperties(
                  parsedQueryStringPersistentLodgingTrackingProperties
                ),
              }
            : {}),
          ...(parsedQueryStringIsTravelApp ? { isTravelApp: "true" } : {}),
        }),
      });

      ({
        parsedQueryString,
        parsedQueryStringFromDate,
        parsedQueryStringUntilDate,
        parsedQueryStringAdultsCount,
        parsedQueryStringChildrenCount,
        parsedQueryStringSelectedLodgingIndex,
      } = parseQueryString(fetchPremierCollectionShopAction, now));
    }
  }

  if (
    !selectedAvailability ||
    !fromDate ||
    !untilDate ||
    fetchPremierCollectionShopAction.options?.overrideStateByQueryParams ||
    fetchPremierCollectionShopAction.options?.forceCallHotelAvailability
  ) {
    const lodgingSelection: LodgingSelection = {
      lodgingIds: [parsedQueryString.lodgingId],
      preserveOrder: true,
      LodgingSelection: LodgingSelectionEnum.LodgingIds,
    };

    const availabilityRequestBody: AvailabilityRequest = {
      lodgingSelection,
      stayDates: {
        from: dayjs(parsedQueryStringFromDate).format("YYYY-MM-DD"),
        until: dayjs(parsedQueryStringUntilDate).format("YYYY-MM-DD"),
      },
      guests: {
        adults: parsedQueryStringAdultsCount,
        children:
          parsedQueryStringChildrenCount && parsedQueryStringChildrenCount > 0
            ? new Array(parsedQueryStringChildrenCount).fill(17)
            : [],
      },
      progressiveConfig: {
        pageSize: 10,
      },
      AvailabilityRequest: AvailabilityRequestEnum.InitialSearch,
      platform: getIsTravelApp().isTravelApp
        ? PlatformEnum.App
        : isMobile()
        ? PlatformEnum.Mobile
        : PlatformEnum.Desktop,
      excludeNonLuxuryLodgings: true,
      rooms: { numberOfRooms: 1 },
    };

    fromDate = parsedQueryStringFromDate;
    untilDate = parsedQueryStringUntilDate;

    yield putResolve(
      availabilityActions.setSelectedLodgingIndex(
        parsedQueryStringSelectedLodgingIndex != null
          ? parsedQueryStringSelectedLodgingIndex
          : selectedLodgingIndex
      )
    );
    yield putResolve(searchActions.setFromDate(fromDate));
    yield putResolve(searchActions.setUntilDate(untilDate));
    yield putResolve(
      searchActions.setOccupancyCounts({
        adults: parsedQueryStringAdultsCount,
        children:
          parsedQueryStringChildrenCount && parsedQueryStringChildrenCount > 0
            ? new Array(parsedQueryStringChildrenCount).fill(17)
            : [],
      })
    );

    yield putResolve(availabilityActions.setSearchedDates(fromDate, untilDate));
    yield putResolve(
      availabilityActions.setSearchedOccupancyCounts({
        adults: parsedQueryStringAdultsCount,
        children:
          parsedQueryStringChildrenCount && parsedQueryStringChildrenCount > 0
            ? new Array(parsedQueryStringChildrenCount).fill(17)
            : [],
      })
    );

    if (parsedQueryString.hotelDetailsEntrySource) {
      yield putResolve(
        actions.setHotelDetailsEntrySource(
          parsedQueryString.hotelDetailsEntrySource
        )
      );
    }

    // Skip the availability re-fetch when matching lodging data is already
    // cached. Checked in two places:
    //   (a) The shop slice's selected lodging (back-nav, re-mount).
    //   (b) The availability slice's city-wide result list (in-app nav from
    //       the availability page — the slice is populated by the availability
    //       saga and contains the lodging the user just clicked).
    // The fetch is only required when neither cache has the lodging at the
    // URL's dates and occupancy, or when a caller explicitly asks to refresh
    // (room picker).
    //
    // Why occupancy is part of the key: the cached Lodging's
    // `price.opaqueShopRequest` token is bound to the availability request
    // that produced it (including guest counts). Reusing it across an
    // occupancy change would call /shop with a token encoding the wrong
    // adults/children, surfacing prices for the prior occupancy.
    //
    // - Shop-slice path uses the pre-saga search-slice counts (captured as
    //   `adultsCount`/`childrenCount` above, before this saga commits the URL
    //   occupancy to state). Those counts were set by whichever flow last
    //   fetched the cached lodging.
    // - Availability-slice path uses the availability slice's
    //   `searchAdultsCount`/`searchChildrenCount`, which are pinned to the
    //   last availability fetch by `setSearchedOccupancyCounts`.
    const cachedFromShopSlice =
      selectedAvailability != null &&
      selectedAvailability.lodging?.id === parsedQueryString.lodgingId &&
      fromDate != null &&
      untilDate != null &&
      dayjs(fromDate).isSame(parsedQueryStringFromDate, "day") &&
      dayjs(untilDate).isSame(parsedQueryStringUntilDate, "day") &&
      adultsCount === parsedQueryStringAdultsCount &&
      childrenCount === parsedQueryStringChildrenCount
        ? selectedAvailability
        : undefined;

    const availabilityLodgings: Lodging[] | undefined = yield select(
      getPremierCollectionAvailabilityLodgings
    );
    const availabilityFromDate: Date | undefined = yield select(
      getPremierCollectionAvailabilityFromDate
    );
    const availabilityUntilDate: Date | undefined = yield select(
      getPremierCollectionAvailabilityUntilDate
    );
    const availabilitySearchAdults: number = yield select(getSearchAdultsCount);
    const availabilitySearchChildren: number = yield select(
      getSearchChildrenCount
    );
    const cachedFromAvailabilitySlice =
      availabilityLodgings != null &&
      availabilityFromDate != null &&
      availabilityUntilDate != null &&
      dayjs(availabilityFromDate).isSame(parsedQueryStringFromDate, "day") &&
      dayjs(availabilityUntilDate).isSame(parsedQueryStringUntilDate, "day") &&
      availabilitySearchAdults === parsedQueryStringAdultsCount &&
      availabilitySearchChildren === parsedQueryStringChildrenCount
        ? availabilityLodgings.find(
            (l) => l.lodging?.id === parsedQueryString.lodgingId
          )
        : undefined;

    const cachedAvailability = cachedFromShopSlice ?? cachedFromAvailabilitySlice;

    let availability: Lodging | undefined;
    if (
      cachedAvailability != null &&
      !fetchPremierCollectionShopAction.options?.forceCallHotelAvailability
    ) {
      availability = cachedAvailability;
    } else {
      const availabilityResponse: { lodgings: Lodging[] } = yield call(
        fetchPremierCollectionAvailability,
        availabilityRequestBody
      );
      availability = availabilityResponse.lodgings.find(
        (l) => l.lodging.id === parsedQueryString.lodgingId
      );
    }
    if (availability) {
      // Filter out PaidBreakfast amenity without mutating the original data
      const filteredAmenities = availability.lodging?.amenities?.filter(
        (amenity) => amenity.amenity !== AmenityEnum.PaidBreakfast
      );

      if (filteredAmenities && filteredAmenities.length !== availability.lodging?.amenities?.length) {
        selectedAvailability = {
          ...availability,
          lodging: {
            ...availability.lodging,
            amenities: filteredAmenities,
          }
        };
      } else {
        selectedAvailability = availability;
      }
      // Only skip re-dispatching selectLodging when reusing the shop slice's
      // own unchanged value - the shop slice's selectedLodging already equals
      // it, so the action would be a no-op subscriber thrash. The
      // availability-slice cache path always needs the dispatch: that value
      // has never been written to the shop slice, so skipping it leaves
      // selectedLodging null and the shop page renders blank.
      if (cachedFromShopSlice !== selectedAvailability) {
        yield putResolve(actions.selectLodging(selectedAvailability));
      }
    } else {
      yield putResolve(actions.selectLodging(selectedAvailability));
    }
  }

  if (parsedQueryStringLocationName) {
    // Hydrate the search bar's location chip asynchronously. This used to be
    // a blocking `yield fetchLocations()` on the critical path, which gated
    // the /hotels/shop request behind a 7s autocomplete call — and the
    // location is only used to populate a chip in the header, not the page.
    yield spawn(hydrateSelectedLocationSaga, parsedQueryStringLocationName);
  }
  if (
    parsedQueryStringShopToken &&
    fetchPremierCollectionShopAction.options?.forceCallHotelAvailability &&
    selectedAvailability.price?.opaqueShopRequest
  ) {
    history.replace({
      ...history.location,
      search: queryStringParser.stringify({
        lodgingId: selectedAvailability.lodging.id,
        fromDate: dayjs(fromDate).format("YYYY-MM-DD"),
        untilDate: dayjs(untilDate).format("YYYY-MM-DD"),
        adultsCount,
        childrenCount,
        selectedLodgingIndex,
        lodgingSelection: encodeURIComponent(
          JSON.stringify(parsedQueryStringLodgingSelection)
        ),
        ...(parsedQueryStringShopToken
          ? { shopToken: selectedAvailability.price?.opaqueShopRequest }
          : {}),
        ...(parsedQueryStringPersistentLodgingTrackingProperties
          ? {
              trackingProperties: encodePersistentLodgingTrackingProperties(
                parsedQueryStringPersistentLodgingTrackingProperties
              ),
            }
          : {}),
        ...(parsedQueryStringIsTravelApp ? { isTravelApp: "true" } : {}),
      }),
    });
    parsedQueryStringShopToken = selectedAvailability.price?.opaqueShopRequest;
  }
  if (parsedQueryStringShopToken) {
    lodgingToken = localCache.get(parsedQueryStringShopToken);
  }

  return { selectedAvailability, shopToken: lodgingToken, isTravelApp: parsedQueryStringIsTravelApp };
}

